import Box, { BoxProps } from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import useResizeObserver from "@react-hook/resize-observer";
import { CSSProperties, ReactNode, useCallback, useId, useRef, useState } from "react";

import { useNextTranslation } from "../hooks";

export type ReadMoreContainerProps = BoxProps & {
    expandOnDoubleClick?: boolean;
    expandLabel?: ReactNode;
    threshold?: number;
    defaultExpanded?: boolean;
    ExpandButtonComponent?: React.ComponentType<ButtonProps>;
};

export const ReadMoreContainer = ({
    expandLabel,
    expandOnDoubleClick = false,
    threshold = 170,
    defaultExpanded = false,
    ...other
}: ReadMoreContainerProps) => {
    const [t] = useNextTranslation("general");
    const defaultExpandLabel = t("readMore");
    return (
        <ExpandableContainer
            threshold={threshold}
            fadeThreshold={30}
            expandOnDoubleClick={expandOnDoubleClick}
            showCollapse={false}
            expandLabel={expandLabel ?? defaultExpandLabel}
            defaultExpanded={defaultExpanded}
            {...other}
        />
    );
};

const ExpandableFadedContainer = styled(Box)<BoxProps>(({ theme }) => ({
    position: "relative",
    "--expandable-container-fade-position": "0px",
    "&:before": {
        content: "''",
        position: "absolute",
        display: "block",
        left: 0,
        top: "var(--expandable-container-fade-position)",
        right: 0,
        bottom: 0,
        backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0), ${theme.palette.background.default} 100%)`,
    },
}));

export type ExpandableContainerProps = BoxProps & {
    threshold?: number;
    fadeThreshold?: number;
    expandOnDoubleClick?: boolean;
    showCollapse?: boolean;
    expandButtonProps?: ButtonProps;
    expandLabel?: ReactNode;
    collapseLabel?: ReactNode;
    defaultExpanded?: boolean;
    ExpandButtonComponent?: React.ComponentType<ButtonProps>;
};
export const ExpandableContainer = ({
    threshold = 400,
    fadeThreshold = 50,
    showCollapse = true,
    expandOnDoubleClick = true,
    expandButtonProps,
    expandLabel,
    collapseLabel,
    ExpandButtonComponent = Button,
    defaultExpanded = false,
    ...other
}: ExpandableContainerProps) => {
    const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
    const [t] = useNextTranslation("general");
    const defaultExpandLabel = t("showMore");
    const defaultCollapseLabel = t("showLess");

    const [expandable, setExpandable] = useState<boolean | null>(null);

    const expand = useCallback(() => {
        setExpanded(true);
    }, []);

    const collapse = useCallback(() => {
        setExpanded(false);
    }, []);

    const contentRef = useRef<HTMLElement | null>(null);
    useResizeObserver(contentRef, (entry) => setExpandable(entry.target.scrollHeight > threshold));

    const id = useId();

    const fadeStyles = { "--expandable-container-fade-position": -fadeThreshold + "px" } as CSSProperties;
    return (
        <>
            <Box
                ref={contentRef}
                id={id}
                overflow="hidden"
                maxHeight={!expanded ? threshold : undefined}
                sx={{
                    opacity: expandable === null ? 0.2 : undefined,
                }}
                onDoubleClick={expandOnDoubleClick ? expand : undefined}
            >
                <Box {...other}></Box>
            </Box>
            {expandable && (showCollapse || !expanded) ? (
                <>
                    <ExpandableFadedContainer
                        aria-hidden="true"
                        style={fadeStyles}
                        display={expanded ? "none" : undefined}
                    ></ExpandableFadedContainer>
                    <ExpandButtonComponent
                        aria-expanded={expanded}
                        aria-controls={id}
                        color="primary"
                        onClick={expanded ? collapse : expand}
                        data-leave-me-alone={true}
                        {...expandButtonProps}
                    >
                        {expanded ? collapseLabel ?? defaultCollapseLabel : expandLabel ?? defaultExpandLabel}
                    </ExpandButtonComponent>
                </>
            ) : null}
        </>
    );
};
